import { datadogRum } from '@datadog/browser-rum';
import { AxiosError, isAxiosError } from 'axios';

async function parseAxiosError(error: AxiosError): Promise<string> {
  if (error.response) {
    const {
      response: { data, status },
      message,
    } = error;

    if (status === 403) {
      return 'Insufficient permissions';
    }

    if (!data) {
      return message;
    }

    if (data && (data as Record<string, string>).message === '') {
      return message;
    }

    if (data && (data as Record<string, string>).message === 'No message available') {
      return message;
    }

    if (Array.isArray(data)) {
      return data
        .map(({ message }) => message)
        .filter(Boolean)
        .join('\n');
    }

    // Multiple errors
    if (typeof data === 'object' && data !== null && 'errors' in data) {
      if (Array.isArray(data.errors)) {
        return data.errors.map(({ message }) => message).join('\n');
      }
    }

    // RFC 7807 Problem Detail response (title/detail instead of message)
    if (typeof data === 'object' && data !== null && 'title' in data) {
      const problemDetail = data as Record<string, string>;
      return problemDetail.detail || problemDetail.title;
    }

    if (data instanceof Blob) {
      const text = await data.text();
      const apiErrorParsed = JSON.parse(text);
      if (apiErrorParsed.message) {
        return apiErrorParsed.message as string;
      }
    }

    return (data as Record<string, string>).message;
  }

  const fingerprint = error.message || 'empty_error_message';
  datadogRum.addError(error, { source: 'parseAxiosError', fingerprint });
  return 'Unknown error';
}

export async function parseError(error: unknown) {
  if (isAxiosError(error)) {
    return parseAxiosError(error);
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Unknown error';
}

export function getErrorStatusCode(error: AxiosError) {
  return error.response?.status;
}

export function isRetryError(error: any) {
  return !!(isAxiosError(error) && error.response && error.response.status >= 500);
}
